import { fileTypeFromBuffer } from 'file-type';

import { DtoContentType, DtoContentTypeReverse } from '@/shared/constants/dto';

/**
 * 파일 타입 체크 결과 타입
 */
export interface FileTypeResult {
  mime: string;
  ext: string;
}

/**
 * File 객체에서 실제 파일 타입을 확인합니다.
 * 파일의 처음 4100바이트를 읽어서 file-type으로 실제 파일 타입을 검증합니다.
 *
 * @param file - 검증할 File 객체
 * @returns 파일 타입 정보 (MIME type과 확장자). 감지 실패 시 null 반환
 */
export async function getFileTypeFromBytes(
  file: File,
): Promise<FileTypeResult | null> {
  try {
    // file-type은 최소 4100바이트를 권장하지만, 작은 파일도 처리 가능
    const chunkSize = Math.min(4100, file.size);
    const chunk = file.slice(0, chunkSize);
    const arrayBuffer = await chunk.arrayBuffer();
    const uint8Array = new Uint8Array(arrayBuffer);

    const result = await fileTypeFromBuffer(uint8Array);

    if (!result) {
      return null;
    }

    return {
      mime: result.mime,
      ext: result.ext,
    };
  } catch (error) {
    console.error('파일 타입 체크 실패:', error);
    return null;
  }
}

/**
 * 파일 타입이 특정 MIME type으로 시작하는지 확인합니다.
 * file-type으로 검증하고, 실패 시 file.type을 fallback으로 사용합니다.
 *
 * @param file - 검증할 File 객체
 * @param expectedMimeType - 예상되는 MIME type (예: 'video/', 'image/', 'audio/')
 * @returns 파일 타입이 예상된 MIME type으로 시작하는지 여부
 */
export async function isFileType(
  file: File,
  expectedMimeType: string,
): Promise<boolean> {
  const fileTypeResult = await getFileTypeFromBytes(file);

  // file-type으로 감지 성공한 경우
  if (fileTypeResult) {
    return fileTypeResult.mime.startsWith(expectedMimeType);
  }

  // 감지 실패 시 기존 file.type을 fallback으로 사용
  return file.type.startsWith(expectedMimeType);
}

/**
 * 파일이 비디오 파일인지 확인합니다.
 *
 * @param file - 검증할 File 객체
 * @returns 비디오 파일 여부
 */
export async function isVideoFile(file: File): Promise<boolean> {
  return isFileType(file, 'video/');
}

/**
 * 파일이 이미지 파일인지 확인합니다.
 *
 * @param file - 검증할 File 객체
 * @returns 이미지 파일 여부
 */
export async function isImageFile(file: File): Promise<boolean> {
  return isFileType(file, 'image/');
}

/**
 * 파일이 오디오 파일인지 확인합니다.
 *
 * @param file - 검증할 File 객체
 * @returns 오디오 파일 여부
 */
export async function isAudioFile(file: File): Promise<boolean> {
  return isFileType(file, 'audio/');
}

/**
 * 파일이 PDF 파일인지 확인합니다 (magic number 기반, 실패 시 file.type fallback).
 *
 * @param file - 검증할 File 객체
 * @returns PDF 파일 여부
 */
export async function isPdfFile(file: File): Promise<boolean> {
  return isFileType(file, 'application/pdf');
}

/**
 * 브라우저가 매핑한 `file.type` (MIME) 이 허용된 ContentType 목록에 속하는지 동기 검증.
 *
 * 용도: `<input accept>` + drag&drop + OS 파일 다이얼로그 "모든 파일" 선택 세 경로에서
 * 공통으로 쓰는 **1차 방어선**. 파일 선택 직후 즉시 반응이 필요한 자리에 사용한다.
 * magic number 기반 심층 검증은 async 함수(`isVideoFile`, `isImageFile`, `isAudioFile`)
 * 를 사용하며 이 함수는 그 앞단 조기 차단용이다.
 *
 * **엣지 케이스**: `file.type` 이 빈 문자열인 경우(macOS 일부 조합의 drag&drop, 확장자
 * 미등록 OS 환경)에는 **차단하지 않고 true 를 반환**한다. 정상 mp4 가 차단되는 거짓 양성
 * 을 피하고, 판정을 async 심층 검증에 위임한다. 이 함수의 역할은 "명확히 잘못된 MIME"
 * 만 조기 거부하는 것.
 *
 * **방어 한계 (FIXME)**: 확장자를 변조한 파일(.mp3 → .mp4 리네임)은 브라우저가
 * `file.type = 'video/mp4'` 로 매핑하므로 이 함수·BE extension 검증 모두 우회 가능.
 * 단, 프로젝트 업로드 경로는 `useSourceFileUpload.addLocalFiles` 내부에서 magic-number
 * 기반 async 검증(`isVideoFile` 등)을 수행하므로 실질 차단이 이뤄진다. 원본 인시던트
 * (정직한 MP3 업로드)는 이 함수로 1차 차단되며, 악의적 리네임은 async 검증에 의존.
 * 완전한 서버측 방어(ProjectLaunchService 의 extension 신뢰 해소)는 별도 보안 티켓.
 *
 * 2026-04-24 인시던트: LTS 에 MP3 업로드 → BE 스토리 생성 실패. 업로드 지점마다 서로 다른
 * 조건으로 하드코딩된 것이 원인이었고, 이 함수로 단일 규칙을 강제한다.
 */
export function isMimeAllowedSync(
  file: File,
  allowedContentTypes: DtoContentType[],
): boolean {
  const fileMime = file.type;
  if (!fileMime) return true; // MIME 미상 → async 심층 검증에 위임
  const allowedPrefixes = allowedContentTypes.map(
    (c) => `${DtoContentTypeReverse[c]}/`,
  );
  return allowedPrefixes.some((prefix) => fileMime.startsWith(prefix));
}
